import React from "react";
import { ArrowRight, FileText, GitBranch, Orbit, Sparkles } from "lucide-react";

type CommonsTool = {
  title: string;
  text: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: "amber" | "cyan" | "green";
};

const COMMONS_TOOLS: CommonsTool[] = [
  {
    title: "Astrology Decision Lens",
    text: "Turn a question, a chart, and a time window into grounded considerations for the decision in front of you.",
    icon: Orbit,
    tone: "amber",
  },
  {
    title: "Document Drafting Studio",
    text: "Turn notes, requirements, and source material into structured school, work, and planning documents you can revise and reuse.",
    icon: FileText,
    tone: "cyan",
  },
  {
    title: "Personal Research Brief",
    text: "Combine your instructions and evidence into a repeatable brief for review, comparison, and clear next actions.",
    icon: Sparkles,
    tone: "green",
  },
];

export const CommonsSection: React.FC = () => (
  <section id="commons" className="landing-commons-section landing-reveal-section">
    <div className="landing-commons__inner">
      <header className="landing-commons__intro">
        <span className="landing-section-kicker">[ The Astral Commons ]</span>
        <h2>Build tools for the questions that matter. Chain them into workflows that work</h2>
        <p>
          Create private, reusable tools in natural language and save them to your account. Use them for astrology,
          decision support, school and work documents, research, and the thinking you return to most.
        </p>
      </header>

      <div className="landing-commons-tool-grid">
        {COMMONS_TOOLS.map(({ title, text, icon: Icon, tone }) => (
          <article className={`landing-commons-tool is-${tone}`} key={title}>
            <div className="landing-commons-tool__topline">
              <Icon aria-hidden="true" />
              <span>Custom tool</span>
            </div>
            <h3>{title}</h3>
            <p>{text}</p>
            <button type="button" onClick={() => window.location.assign("/observatory")}>
              Open in the Observatory
              <ArrowRight aria-hidden="true" />
            </button>
          </article>
        ))}
      </div>

      <div className="landing-commons-flow">
        <div className="landing-commons-flow__heading">
          <div>
            <span className="landing-section-kicker">From tool to shared value</span>
            <h3>Compose a workflow, save the method, and let useful work travel</h3>
          </div>
          <GitBranch aria-hidden="true" />
        </div>
        <div className="landing-commons-flow__steps" aria-label="Tool workflow sequence">
          <span><b>01</b>Describe the tool</span>
          <i aria-hidden="true" />
          <span><b>02</b>Save it to your account</span>
          <i aria-hidden="true" />
          <span><b>03</b>Chain tools into a workflow</span>
          <i aria-hidden="true" />
          <span><b>04</b>Share and qualify for revenue</span>
        </div>
        <p className="landing-commons-flow__note">
          When members pay to use eligible tools or workflows you create, you can participate in the revenue they generate.
        </p>
      </div>

      <div className="landing-forge-banner landing-commons-forge-banner">
        <span><strong>Build your own instrument:</strong> Start with a natural-language idea and shape it into something reusable.</span>
        <button type="button" onClick={() => window.location.assign("/observatory")}>Open Forge <ArrowRight aria-hidden="true" /></button>
      </div>
    </div>
  </section>
);
